import React from "react";
import { motion } from "framer-motion";

const skills = [
  // Frontend
  {
    src: "https://raw.githubusercontent.com/devicons/devicon/master/icons/react/react-original.svg",
    label: "React",
    color: "text-blue-400",
    category: "Frontend",
  },
  {
    src: "https://raw.githubusercontent.com/devicons/devicon/master/icons/redux/redux-original.svg",
    label: "Redux",
    color: "text-purple-600",
    category: "Frontend",
  },
  {
    src: "https://raw.githubusercontent.com/devicons/devicon/master/icons/html5/html5-original.svg",
    label: "HTML",
    color: "text-orange-500",
    category: "Frontend",
  },
  {
    src: "https://raw.githubusercontent.com/devicons/devicon/master/icons/css3/css3-original.svg",
    label: "CSS",
    color: "text-blue-600",
    category: "Frontend",
  },
  {
    src: "https://raw.githubusercontent.com/devicons/devicon/master/icons/javascript/javascript-original.svg",
    label: "JavaScript",
    color: "text-yellow-500",
    category: "Frontend",
  },
  {
    src: "https://raw.githubusercontent.com/devicons/devicon/master/icons/typescript/typescript-original.svg",
    label: "TypeScript",
    color: "text-blue-500",
    category: "Frontend",
  },
  {
    src: "https://raw.githubusercontent.com/devicons/devicon/master/icons/tailwindcss/tailwindcss-plain.svg",
    label: "Tailwind CSS",
    color: "text-cyan-400",
    category: "Frontend",
  },
  {
    src: "https://raw.githubusercontent.com/devicons/devicon/master/icons/materialui/materialui-original.svg",
    label: "Material UI",
    color: "text-blue-800",
    category: "Frontend",
  },
  {
    src: "https://raw.githubusercontent.com/devicons/devicon/master/icons/bootstrap/bootstrap-original.svg",
    label: "Bootstrap",
    color: "text-purple-600",
    category: "Frontend",
  },

  // Backend
  {
    src: "https://raw.githubusercontent.com/devicons/devicon/master/icons/express/express-original.svg",
    label: "Express",
    color: "text-gray-200",
    extraClass: "bg-white rounded-full p-1",
    category: "Backend",
  },
  {
    src: "https://raw.githubusercontent.com/devicons/devicon/master/icons/nodejs/nodejs-original.svg",
    label: "Node.js",
    color: "text-green-300",
    category: "Backend",
  },

  // Database
  {
    src: "https://raw.githubusercontent.com/devicons/devicon/master/icons/mongodb/mongodb-original.svg",
    label: "MongoDB",
    color: "text-green-400",
    category: "Database",
  },
  {
    src: "https://raw.githubusercontent.com/devicons/devicon/master/icons/mysql/mysql-original.svg",
    label: "SQL",
    color: "text-blue-600",
    category: "Database",
  },

  // Version Control
  {
    src: "https://raw.githubusercontent.com/devicons/devicon/master/icons/git/git-original.svg",
    label: "Git",
    color: "text-orange-600",
    category: "Version Control",
  },
  {
    src: "https://raw.githubusercontent.com/devicons/devicon/master/icons/github/github-original.svg",
    label: "GitHub",
    color: "text-white",
    category: "Version Control",
  },

  // Tools
  {
    src: "https://www.vectorlogo.zone/logos/getpostman/getpostman-icon.svg",
    label: "Postman",
    color: "text-orange-500",
    category: "Tools",
  },
];

const categories = [
  "Frontend",
  "Backend",
  "Database",
  "Version Control",
  "Tools",
];

// Animation variants
const cardVariants = {
  hidden: { opacity: 0, y: 30, scale: 0.9 },
  visible: { opacity: 1, y: 0, scale: 1, transition: { duration: 0.5 } },
};

const headerVariants = {
  hidden: { opacity: 0, x: -50 },
  visible: { opacity: 1, x: 0, transition: { duration: 0.6 } },
};

const SkillSection = () => {
  return (
    <div className="px-6 sm:px-10 lg:px-20 py-16 bg-gradient-to-b from-black via-gray-900 to-black">
      <motion.h3
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.7 }}
        className="text-white font-extrabold text-4xl sm:text-5xl text-center mb-14 tracking-wide"
      >
        Skills
      </motion.h3>

      {categories.map((category, i) => (
        <div key={category} className="mb-16">
          {/* Category Header */}
          <motion.div
            variants={headerVariants}
            initial="hidden"
            animate="visible"
            className="flex items-center gap-4 mb-8"
          >
            <h4 className="text-white text-2xl sm:text-3xl font-bold">
              {category}
            </h4>
            <div className="flex-1 border-t border-gray-700"></div>
          </motion.div>

          {/* Skills Grid */}
          <motion.div
            className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-6"
            initial="hidden"
            animate="visible"
            variants={{
              hidden: {},
              visible: { transition: { staggerChildren: 0.15 } },
            }}
          >
            {skills
              .filter((skill) => skill.category === category)
              .map((skill, index) => (
                <motion.div
                  key={index}
                  variants={cardVariants}
                  whileHover={{
                    scale: 1.08,
                    rotate: 2,
                    boxShadow: "0px 0px 20px rgba(255,255,255,0.4)",
                  }}
                  whileTap={{ scale: 0.95 }}
                  className="p-5 bg-white/10 backdrop-blur-md rounded-xl shadow-lg flex flex-col items-center cursor-pointer"
                >
                  <motion.img
                    src={skill.src}
                    alt={skill.label}
                    className={`w-14 h-14 ${skill.extraClass || ""}`}
                    whileHover={{ scale: 1.15, rotate: -5 }}
                    transition={{ type: "spring", stiffness: 300 }}
                  />
                  <span
                    className={`mt-3 text-sm sm:text-base font-semibold ${skill.color}`}
                  >
                    {skill.label}
                  </span>
                </motion.div>
              ))}
          </motion.div>
        </div>
      ))}
    </div>
  );
};

export default SkillSection;
